import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { classTestsService, type ClassTest, type ClassTestRosterEntry } from "@/lib/services";
import { useAuth } from "@/lib/auth";
import { byRollNo } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, Plus, Pencil, Save, Trash2, ClipboardList } from "lucide-react";
import { toast } from "sonner";


export function ClassTestsPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isPrivileged = user?.role === "admin" || user?.role === "principal" || user?.role === "teacher" || user?.role === "examhead";

  const { data: tests = [], isPending } = useQuery({
    queryKey: ["class-tests", "list"],
    queryFn: () => classTestsService.list(),
    enabled: isPrivileged,
  });
  const { data: myTests = [], isPending: myLoading } = useQuery({
    queryKey: ["class-tests", "mine"],
    queryFn: () => classTestsService.mine(),
    enabled: !isPrivileged,
  });

  const [creating, setCreating] = useState(false);
  const [active, setActive] = useState<ClassTest | null>(null);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Class Tests</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {isPrivileged
              ? `${tests.length} test(s) — quick topic-wise assessments`
              : `${myTests.length} test result(s)`}
          </p>
        </div>
        {isPrivileged && (
          <Button size="sm" onClick={() => setCreating(true)}>
            <Plus className="mr-2 h-4 w-4" /> New test
          </Button>
        )}
      </div>

      {isPrivileged ? (
        <Card>
          <CardContent className="p-0">
            {isPending
              ? <div className="flex h-24 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
              : tests.length === 0
                ? <div className="p-8 text-center text-sm text-muted-foreground">No class tests yet. Click <strong>New test</strong> to create one.</div>
                : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Class</TableHead>
                        <TableHead>Subject</TableHead>
                        <TableHead>Topic / Subtopic</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead className="text-right">Max</TableHead>
                        <TableHead>Conducted by</TableHead>
                        <TableHead className="w-24" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {tests.map((t) => (
                        <TableRow key={t.id} className="cursor-pointer" onClick={() => setActive(t)}>
                          <TableCell className="font-semibold">{t.class_name}</TableCell>
                          <TableCell>{t.subject}</TableCell>
                          <TableCell>
                            <div>{t.topic}</div>
                            {t.subtopic && <div className="text-xs text-muted-foreground">{t.subtopic}</div>}
                          </TableCell>
                          <TableCell className="text-muted-foreground">{t.conducted_on}</TableCell>
                          <TableCell className="text-right tabular-nums">{t.total_marks}</TableCell>
                          <TableCell className="text-muted-foreground">{t.conducted_by_name}</TableCell>
                          <TableCell><Button size="sm" variant="ghost"><Pencil className="mr-1 h-3 w-3" /> Marks</Button></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
          </CardContent>
        </Card>
      ) : (
        // Student / parent view
        <div className="grid gap-4 md:grid-cols-2">
          {myLoading ? <Loader2 className="h-6 w-6 animate-spin text-primary" /> :
           myTests.length === 0 ? (
             <div className="text-sm text-muted-foreground">No class test results yet.</div>
           ) : myTests.map(({ test, mark }) => {
            const pct = mark.marks_obtained != null && test.total_marks
              ? (mark.marks_obtained / test.total_marks) * 100 : null;
            return (
              <Card key={test.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">{test.topic}</CardTitle>
                    {pct != null && <Badge variant="outline">{pct.toFixed(0)}%</Badge>}
                  </div>
                  <CardDescription>
                    {test.subject} · {test.class_name} · {test.conducted_on}
                    {test.subtopic && <> · <span className="italic">{test.subtopic}</span></>}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-3 text-2xl font-bold">
                    {mark.is_absent ? <span className="text-amber-600">Absent</span> : (
                      <>
                        <span className="text-emerald-700">{mark.marks_obtained ?? "—"}</span>
                        <span className="text-sm text-muted-foreground">/ {test.total_marks}</span>
                      </>
                    )}
                  </div>
                  {mark.remark && <p className="mt-2 text-sm text-muted-foreground">💬 {mark.remark}</p>}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {creating && <CreateTestDialog onClose={() => { setCreating(false); qc.invalidateQueries({ queryKey: ["class-tests"] }); }} />}
      {active && <MarksEntryDialog test={active} onClose={() => { setActive(null); qc.invalidateQueries({ queryKey: ["class-tests"] }); }} />}
    </div>
  );
}

function CreateTestDialog({ onClose }: { onClose: () => void }) {
  const [className, setClassName] = useState("Grade 5A");
  const [subject, setSubject] = useState("");
  const [topic, setTopic] = useState("");
  const [subtopic, setSubtopic] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [totalMarks, setTotalMarks] = useState("20");
  const [remarks, setRemarks] = useState("");

  const mut = useMutation({
    mutationFn: () => classTestsService.create({
      class_name: className, subject, topic, subtopic: subtopic || undefined,
      conducted_on: date, total_marks: Number(totalMarks) || 20,
      remarks: remarks || undefined,
    }),
    onSuccess: () => { toast.success("Test created"); onClose(); },
    onError: (e: any) => toast.error(e.message),
  });

  const canSave = className && subject && topic && date && Number(totalMarks) > 0;

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>New class test</DialogTitle>
          <DialogDescription>Log a short topic-wise test. Marks visible to student/parent immediately.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Class</Label><Input value={className} onChange={(e) => setClassName(e.target.value)} placeholder="Grade 5A" /></div>
            <div><Label>Subject</Label><Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Mathematics" /></div>
          </div>
          <div><Label>Topic</Label><Input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="Fractions" /></div>
          <div><Label>Sub-topic (optional)</Label><Input value={subtopic} onChange={(e) => setSubtopic(e.target.value)} placeholder="Addition of unlike fractions" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></div>
            <div><Label>Max marks</Label><Input type="number" min={1} max={200} value={totalMarks} onChange={(e) => setTotalMarks(e.target.value)} /></div>
          </div>
          <div><Label>Remarks (optional)</Label><Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={2} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => mut.mutate()} disabled={!canSave || mut.isPending}>
            {mut.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MarksEntryDialog({ test, onClose }: { test: ClassTest; onClose: () => void }) {
  const { data, isPending } = useQuery({
    queryKey: ["class-tests", "detail", test.id],
    queryFn: () => classTestsService.detail(test.id),
  });
  const [rows, setRows] = useState<Record<string, ClassTestRosterEntry>>({});
  useEffect(() => {
    if (data?.roster) {
      const map: Record<string, ClassTestRosterEntry> = {};
      data.roster.forEach((r) => { map[r.student_id] = { ...r }; });
      setRows(map);
    }
  }, [data?.roster]);

  const save = useMutation({
    mutationFn: () => classTestsService.saveMarks(test.id, Object.values(rows).map((r) => ({
      student_id: r.student_id,
      marks_obtained: r.is_absent ? null : (r.marks_obtained ?? null),
      is_absent: r.is_absent,
      remark: r.remark || undefined,
    }))),
    onSuccess: (r) => { toast.success(`Saved marks for ${r.upserted} student(s)`); onClose(); },
    onError: (e: any) => toast.error(e.message),
  });

  const del = useMutation({
    mutationFn: () => classTestsService.remove(test.id),
    onSuccess: () => { toast.success("Test deleted"); onClose(); },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-primary" /> {test.topic}
          </DialogTitle>
          <DialogDescription>
            {test.class_name} · {test.subject} · {test.conducted_on} · Max {test.total_marks}
            {test.subtopic && <> · <em>{test.subtopic}</em></>}
          </DialogDescription>
        </DialogHeader>
        {isPending ? <Loader2 className="mx-auto h-6 w-6 animate-spin text-primary" /> : (
          <div className="max-h-[65vh] overflow-y-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-muted/80">
                <tr className="text-left">
                  <th className="p-2 w-16">Roll</th>
                  <th className="p-2">Student</th>
                  <th className="p-2 text-right w-24">Marks /{test.total_marks}</th>
                  <th className="p-2 w-16 text-center">Absent</th>
                  <th className="p-2">Remark</th>
                </tr>
              </thead>
              <tbody>
                {Object.values(rows).sort(byRollNo).map((r) => (
                  <tr key={r.student_id} className={`border-t ${r.is_absent ? "opacity-50" : ""}`}>
                    <td className="p-2 text-muted-foreground">{r.roll_no}</td>
                    <td className="p-2 font-semibold">{r.student_name}</td>
                    <td className="p-1 text-right">
                      <Input type="number" min={0} max={test.total_marks} disabled={r.is_absent}
                             className="h-8 w-24 text-right ml-auto"
                             value={r.marks_obtained ?? ""}
                             onChange={(e) => setRows((p) => ({
                               ...p, [r.student_id]: { ...p[r.student_id], marks_obtained: e.target.value === "" ? null : Number(e.target.value) },
                             }))} />
                    </td>
                    <td className="p-2 text-center">
                      <input type="checkbox" checked={!!r.is_absent}
                             onChange={(e) => setRows((p) => ({
                               ...p, [r.student_id]: { ...p[r.student_id], is_absent: e.target.checked },
                             }))} />
                    </td>
                    <td className="p-1">
                      <Input className="h-8" placeholder="—"
                             value={r.remark ?? ""}
                             onChange={(e) => setRows((p) => ({
                               ...p, [r.student_id]: { ...p[r.student_id], remark: e.target.value },
                             }))} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" className="mr-auto" onClick={() => del.mutate()} disabled={del.isPending}>
            <Trash2 className="mr-2 h-4 w-4 text-destructive" /> Delete test
          </Button>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending || isPending}>
            {save.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Save marks
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
